// @flow
import {
  format,
  formatDistanceStrict,
  isAfter,
  isSameDay,
  addHours,
  parseISO
} from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import ptLocale from 'date-fns/locale/pt';
import { IDEALIST_TIME_ZONE } from 'utils/constants';
import moment from 'moment-timezone';

export const LOCALES = {
  en: enLocale,
  es: esLocale,
  pt: ptLocale
};

const SHORT_DATE_FORMAT = {
  en: 'MM/dd/yyyy',
  es: 'dd/MM/yyyy',
  pt: 'dd/MM/yyyy'
};

const getDate = (date: string | number | Date) =>
  typeof date === 'string' ? parseISO(date) : date;

// FIXME: deprecated - replace with shorterDate and update tests
export const shortDate = (date: string | number | Date) =>
  format(getDate(date), SHORT_DATE_FORMAT[USER_LOCALE]);

const SHORTER_DATE_FORMAT = {
  en: 'M/d/yyyy',
  es: 'd/M/yyyy',
  pt: 'd/M/yyyy'
};

export const shorterDate = (date: string | number | Date) =>
  format(getDate(date), SHORTER_DATE_FORMAT[USER_LOCALE]);

const SHORTEST_DATE_FORMAT = {
  en: 'M/d',
  es: 'd/M',
  pt: 'd/M'
};

export const shortestDate = (date: string | number | Date) =>
  format(getDate(date), SHORTEST_DATE_FORMAT[USER_LOCALE]);

const SHORTEST_DATE_WITH_YEAR_FORMAT = {
  en: 'M/d/yy',
  es: 'd/M/yy',
  pt: 'd/M/yy'
};

export const shortestDateWithYear = (date: string | number | Date) =>
  format(getDate(date), SHORTEST_DATE_WITH_YEAR_FORMAT[USER_LOCALE]);

const SHORTER_TIME_FORMAT_MOMENT = {
  en: 'h:mma',
  es: 'HH:mm',
  pt: 'HH:mm'
};

export const shorterTime = (date: string | number | Date, timezone?: ?string) =>
  timezone
    ? moment(date)
        .tz(timezone)
        .format(SHORTER_TIME_FORMAT_MOMENT[USER_LOCALE])
    : moment(date).format(SHORTER_TIME_FORMAT_MOMENT[USER_LOCALE]);

export const shortTimezone = (
  date: ?(string | number | Date),
  timezone: ?string
) => {
  const m = date ? moment(date) : moment();
  const momentDate = m.tz(timezone);
  const abbr = momentDate.format('z');
  if (!abbr.match(/[0-9]/)) {
    return abbr;
  }
  const offset = momentDate.format('Z');
  return `UTC${offset.replace(':00', '')}`;
};

const DATETIME_FORMAT = {
  en: 'MM/dd/yyyy  hh:mma',
  es: 'dd/MM/yyyy HH:mm',
  pt: 'dd/MM/yyyy HH:mm'
};

export const dateTime = (date: string | number | Date) =>
  format(getDate(date), DATETIME_FORMAT[USER_LOCALE]);

const SHORT_DATETIME_FORMAT = {
  en: 'M/d/yy h:mma',
  es: 'd/M/yy HH:mm',
  pt: 'd/M/yy HH:mm'
};

export const shortDateTime = (date: string | number | Date) =>
  format(getDate(date), SHORT_DATETIME_FORMAT[USER_LOCALE]);

const HUMAN_DATE_FORMAT_MOMENT = {
  en: 'MMMM D, YYYY',
  es: 'D MMMM YYYY',
  pt: 'D MMMM YYYY'
};

export const humanDate = (date: string | number | Date, timezone?: ?string) =>
  timezone
    ? moment(date)
        .tz(timezone)
        .format(HUMAN_DATE_FORMAT_MOMENT[USER_LOCALE])
    : format(getDate(date), 'MMMM d, yyyy', {
        locale: LOCALES[USER_LOCALE]
      });

const HUMAN_DATETIME_FORMAT = {
  en: 'MMM d, yyyy h:mm a',
  es: 'MMM d, yyyy H:mm',
  pt: 'MMM d, yyyy H:mm'
};

export const humanDateTime = (date: string | number | Date) =>
  format(getDate(date), HUMAN_DATETIME_FORMAT[USER_LOCALE], {
    locale: LOCALES[USER_LOCALE]
  });

export const humanTime = (time: string) =>
  moment(time, 'H:m:s').format(SHORTER_TIME_FORMAT_MOMENT[USER_LOCALE]);

export const monthAndYear = (date: string | number | Date) =>
  format(getDate(date), 'MMMM yyyy', {
    locale: LOCALES[USER_LOCALE]
  });

export const dayAndMonth = (date: string | number | Date) =>
  format(getDate(date), 'MMM d', {
    locale: LOCALES[USER_LOCALE]
  });

export const dayNumber = (date: string | number | Date) =>
  format(getDate(date), 'd', {
    locale: LOCALES[USER_LOCALE]
  });

export const shortMonth = (date: string | number | Date) =>
  format(getDate(date), 'MMM', {
    locale: LOCALES[USER_LOCALE]
  }).slice(0, 3);

export const yearOnly = (date: string | number | Date) =>
  format(getDate(date), 'yyyy', {
    locale: LOCALES[USER_LOCALE]
  });

export const now = () => new Date();

export const nowMs = () => now().getTime();

export const daysInMs = (days: number) => 1000 * 60 * 60 * 24 * days;

export const distanceInWordsToNow = (date: string | number | Date) =>
  formatDistanceStrict(getDate(date), new Date(), {
    addSuffix: true,
    locale: LOCALES[USER_LOCALE],
    roundingMethod: 'floor'
  });

const hasSeconds = (time: string) => time.split(':').length === 3;

export const withSeconds = (time: string) =>
  hasSeconds(time) ? time : `${time}:00`;

// Converts a date or date string to GMT timezone
export const toGMTDate = (date: string | Date) => {
  date = new Date(date);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const stripTimezone = (dateString: string) =>
  dateString.replace('Z', '');

// Take a timezone-aware date and return the user's date / time (not timezone-aware)
// as a string
export const formatLocalDate = (d: Date): string => {
  const convertedDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return stripTimezone(convertedDate.toISOString());
};

// @TODO: Pass in tz argument, so we can format any date to
// any timezone... or use a library ;)
export const formatIdealistDateTime = (d: Date | string): Date => {
  const options = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: IDEALIST_TIME_ZONE
  };
  const formattedDateParts: Array<{
    type: string,
    value: string
  }> = new Intl.DateTimeFormat('en-us', options).formatToParts(d);
  const dateTimeParams = formattedDateParts
    .filter(datePart =>
      ['year', 'month', 'day', 'hour', 'minute', 'second'].includes(
        datePart.type
      )
    )
    .map(datePart => parseInt(datePart.value, 10));
  const [month, day, year, hour, minute, second] = dateTimeParams;
  return new Date(year, month - 1, day, hour, minute, second);
};

const getLocalizedToday = (date: string | Date) => {
  const offsetMatch =
    typeof date === 'string'
      ? date.match(/([+-]?[0-9]+)(?::[0-9]+)?$/)
      : date.getTimezoneOffset();
  const offsetToDate =
    typeof offsetMatch === 'number'
      ? offsetMatch
      : offsetMatch
      ? parseInt(offsetMatch.pop(), 10)
      : 0;
  const today = new Date();
  const offsetFromNow = today.getTimezoneOffset() / 60;
  const offsetTotal = offsetToDate + offsetFromNow;
  return addHours(today, offsetTotal);
};

export const dateIsToday = (date: string | Date) =>
  isSameDay(getLocalizedToday(date), getDate(date));

export const dateIsUpcoming = (date: string | Date) => {
  const today = getLocalizedToday(date);
  return isAfter(getDate(date), today) || isSameDay(today, getDate(date));
};

export const dateIsPast = (date: string | Date) => !dateIsUpcoming(date);

export const combineDateTime = (
  date: string,
  time: string,
  timezone?: ?string
) => {
  const dateTimeString = `${date} ${time}`;
  const formattedDate = timezone
    ? moment.tz(dateTimeString, timezone).format()
    : moment(dateTimeString).format();
  return formattedDate;
};

export const getCurrentYear = () => moment().format('YYYY');
